/**
 * Accessibility utility functions for the McKenzie Portal application
 * Provides helper functions for ARIA labels, focus management, and keyboard navigation
 */

/**
 * Generates appropriate ARIA labels for interactive elements
 * @param action - The action being performed
 * @param context - Optional context information
 * @param state - Optional state information (e.g., "expanded", "collapsed")
 * @returns Formatted ARIA label string
 */
export const getAriaLabel = (
  action: string, 
  context?: string, 
  state?: string
): string => {
  let label = action;
  
  if (context) {
    label = `${action} ${context}`;
  }
  
  if (state) {
    label = `${label}, ${state}`;
  }
  
  return label;
};

/**
 * Gets all focusable elements within a container
 * @param container - The container element to search within
 * @returns NodeList of focusable elements
 */
export const getFocusableElements = (container: HTMLElement): NodeListOf<Element> => {
  return container.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
};

/**
 * Manages focus within a modal or dropdown
 * @param container - The container element
 * @param firstFocusable - Optional first focusable element
 * @param lastFocusable - Optional last focusable element
 */
export const trapFocus = (
  container: HTMLElement,
  firstFocusable?: Element,
  lastFocusable?: Element
): (() => void) => {
  const focusableElements = getFocusableElements(container);
  const firstElement = firstFocusable || focusableElements[0];
  const lastElement = lastFocusable || focusableElements[focusableElements.length - 1];

  const handleTabKey = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      // Shift + Tab (backward)
      if (document.activeElement === firstElement) {
        (lastElement as HTMLElement)?.focus();
        e.preventDefault();
      }
    } else {
      // Tab (forward)
      if (document.activeElement === lastElement) {
        (firstElement as HTMLElement)?.focus();
        e.preventDefault();
      }
    }
  };

  document.addEventListener('keydown', handleTabKey);

  // Return cleanup function
  return () => {
    document.removeEventListener('keydown', handleTabKey);
  };
};

/**
 * Announces text to screen readers
 * @param message - The message to announce
 * @param priority - Priority level ('polite' or 'assertive')
 */
export const announceToScreenReader = (
  message: string, 
  priority: 'polite' | 'assertive' = 'polite'
): void => {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.style.position = 'absolute';
  announcement.style.left = '-10000px';
  announcement.style.width = '1px';
  announcement.style.height = '1px';
  announcement.style.overflow = 'hidden';
  
  document.body.appendChild(announcement);
  announcement.textContent = message;
  
  // Clean up after announcement
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

/**
 * Checks if an element is visible to screen readers
 * @param element - The element to check
 * @returns Boolean indicating visibility
 */
export const isVisibleToScreenReader = (element: HTMLElement): boolean => {
  const style = window.getComputedStyle(element);
  const isHidden = style.display === 'none' || 
                   style.visibility === 'hidden' || 
                   element.hidden ||
                   element.getAttribute('aria-hidden') === 'true';
  
  return !isHidden;
};

/**
 * Generates unique IDs for form elements
 * @param prefix - Prefix for the ID
 * @returns Unique ID string
 */
export const generateId = (prefix: string = 'element'): string => {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Validates color contrast ratio
 * @param foreground - Foreground color (hex, rgb, or hsl)
 * @param background - Background color (hex, rgb, or hsl)
 * @param level - WCAG level ('AA' or 'AAA')
 * @returns Boolean indicating if contrast meets requirements
 */
export const validateColorContrast = (
  foreground: string,
  background: string,
  level: 'AA' | 'AAA' = 'AA'
): boolean => {
  // Convert colors to RGB
  const fgRgb = hexToRgb(foreground);
  const bgRgb = hexToRgb(background);
  
  if (!fgRgb || !bgRgb) return false;
  
  // Calculate relative luminance
  const fgLuminance = getRelativeLuminance(fgRgb);
  const bgLuminance = getRelativeLuminance(bgRgb);
  
  // Calculate contrast ratio
  const lighter = Math.max(fgLuminance, bgLuminance);
  const darker = Math.min(fgLuminance, bgLuminance);
  const contrastRatio = (lighter + 0.05) / (darker + 0.05);
  
  // Check against WCAG requirements
  const requiredRatio = level === 'AA' ? 4.5 : 7;
  return contrastRatio >= requiredRatio;
};

/**
 * Converts hex color to RGB
 * @param hex - Hex color string
 * @returns RGB object or null if invalid
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Calculates relative luminance of an RGB color
 * @param rgb - RGB color object
 * @returns Relative luminance value
 */
const getRelativeLuminance = (rgb: { r: number; g: number; b: number }): number => {
  const { r, g, b } = rgb;
  
  // Convert to sRGB
  const rsRGB = r / 255;
  const gsRGB = g / 255;
  const bsRGB = b / 255;
  
  // Apply gamma correction
  const rLinear = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
  const gLinear = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
  const bLinear = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);
  
  // Calculate relative luminance
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
};

/**
 * Keyboard event handlers for common interactions
 */
export const keyboardHandlers = {
  /**
   * Handles Enter and Space key presses for button-like elements
   */
  onActivate: (callback: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  },
  
  /**
   * Handles Escape key for closing modals/dropdowns
   */
  onEscape: (callback: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      callback();
    }
  },
  
  /**
   * Handles arrow key navigation
   */
  onArrowKeys: (
    onUp?: () => void,
    onDown?: () => void,
    onLeft?: () => void,
    onRight?: () => void
  ) => (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        onUp?.();
        break;
      case 'ArrowDown':
        e.preventDefault();
        onDown?.();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        onLeft?.();
        break;
      case 'ArrowRight':
        e.preventDefault();
        onRight?.();
        break;
    }
  }
};
